import type { FC, ReactNode } from 'react'

import { Plus } from 'lucide-react'
import { SectionHeading } from '@/components/landing/section-heading'
import { siteConfig } from '@/constants/site'

const faqs = [
    {
        question: "Is your porridge gluten-free?",
        answer: "The porridge itself is made only with rice, chicken, ginger and water, so it is naturally gluten-free. Our soy sauce and fried dough sticks are not, so let us know and we will leave them off."
    },
    {
        question: "Can I order for a group?",
        answer: "Yes. The Family Pot serves four and comes with every topping packed on the side. For more than ten bowls, call us a day ahead and we will cook a pot just for you."
    },
    {
        question: "How do I reheat leftovers?",
        answer: "Porridge thickens as it cools. Warm it gently on the stove with a splash of water, stirring until it turns silky again. It keeps for up to three days in the fridge."
    },
    {
        question: "Can you make it milder for kids?",
        answer: "Our Little Bowl is gently seasoned for younger appetites, and any bowl can be made with less salt or no pepper. Just add a note to your order."
    },
    {
        question: "Do you have vegetarian options?",
        answer: "Every bowl is cooked in our chicken broth, so the porridge is not vegetarian. Our sides and drinks are, apart from the extra pulled chicken."
    },
    {
        question: "Do I need to pay online?",
        answer: "No. Order ahead here and pay at the counter when you pick up, by card or cash."
    }
]

export const FaqSection: FC = (): ReactNode => {
    return (
        <section id='faq' aria-labelledby='faq-title' className='scroll-mt-20 pb-24 sm:pb-32'>
            <div className='mx-auto grid max-w-6xl gap-10 px-5 sm:px-8 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16'>
                <SectionHeading
                    titleId='faq-title'
                    eyebrow="Good to know"
                    title={<>Questions, <em>answered.</em></>}
                    description={(
                        <>
                            Something we missed? Call us at{' '}
                            <a
                                href={siteConfig.phoneHref}
                                className='font-semibold text-foreground underline decoration-foreground/25 underline-offset-4 hover:decoration-foreground'
                            >
                                {siteConfig.phone}
                            </a>
                            {' '}and we will happily talk porridge.
                        </>
                    )}
                />

                <div className='divide-y divide-border border-y border-border'>
                    {faqs.map(faq => (
                        <details key={faq.question} className='group'>
                            <summary className='flex cursor-pointer list-none items-center justify-between gap-6 rounded-lg py-5 text-left font-heading text-xl font-semibold outline-none transition-colors hover:text-primary focus-visible:ring-2 focus-visible:ring-ring'>
                                {faq.question}

                                <span
                                    aria-hidden='true'
                                    className='inline-flex size-9 shrink-0 items-center justify-center rounded-full border border-border text-foreground transition-[transform,background-color,border-color,color] duration-300 group-open:rotate-45 group-open:border-primary group-open:bg-primary group-open:text-primary-foreground'
                                >
                                    <Plus className='size-4' />
                                </span>
                            </summary>

                            <p className='pb-6 pr-12 leading-relaxed text-muted-foreground'>
                                {faq.answer}
                            </p>
                        </details>
                    ))}
                </div>
            </div>
        </section>
    )
}
